import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box, Container, Avatar, Typography, Stack, IconButton,
  Paper, Skeleton, Alert, Divider, Link, Tooltip, Button,
  Chip, CircularProgress, Dialog, DialogContent, DialogActions, TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PublicIcon from '@mui/icons-material/Public';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CloseIcon from '@mui/icons-material/Close';
import GridOnIcon from '@mui/icons-material/GridOn';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import profileApi from './profileApi';
import networkApi from '../Network/services/networkApi';
import momentsApi from '../Moments/momentsApi';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import AddToCirclePrompt from '../../components/network/AddToCirclePrompt';

function photoKey(eventId, photoId) {
  return `${eventId}:${photoId}`;
}

function timeAgo(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function PhotoCommentsPanel({ eventId, photoId, onCommentCountDelta }) {
  const [comments, setComments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    momentsApi.getComments(eventId, photoId)
      .then((payload) => {
        if (!cancelled) setComments(Array.isArray(payload.comments) ? payload.comments : []);
      })
      .catch(() => { if (!cancelled) setComments([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventId, photoId]);

  const handleSubmit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const result = await momentsApi.postComment(eventId, photoId, { commentText: text });
      setComments((prev) => ([...(prev || []), {
        comment_id: result.comment_id,
        comment_text: text,
        visibility_scope: 'public',
        created_at: new Date().toISOString(),
        author_name: 'You',
        can_delete: true,
      }]));
      setDraft('');
      onCommentCountDelta(1);
    } catch (err) {
      // best-effort — comment box keeps the draft so the user can retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId) => {
    const prev = comments;
    setComments((cur) => (cur || []).filter((c) => c.comment_id !== commentId));
    try {
      await momentsApi.deleteComment(eventId, photoId, commentId);
      onCommentCountDelta(-1);
    } catch (err) {
      setComments(prev);
    }
  };

  return (
    <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
      {loading ? (
        <Stack spacing={0.75} sx={{ py: 1 }}>
          {[0, 1].map((i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Skeleton variant="circular" width={26} height={26} />
              <Skeleton variant="text" width="70%" />
            </Stack>
          ))}
        </Stack>
      ) : comments.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
          No comments yet — be the first to say something.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />} sx={{ maxHeight: 220, overflowY: 'auto' }}>
          {comments.map((comment) => (
            <Stack key={comment.comment_id} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.5 }}>
              <Avatar src={comment.author_avatar || undefined} sx={{ width: 26, height: 26, fontSize: 12, mt: 0.25 }}>
                {(comment.author_name || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" fontWeight={800}>{comment.author_name}</Typography>
                  <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{comment.comment_text}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {timeAgo(comment.created_at)}
                </Typography>
              </Box>
              {comment.can_delete && (
                <IconButton size="small" onClick={() => handleDelete(comment.comment_id)} aria-label="Delete comment" sx={{ p: 0.5 }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Add a comment..."
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
          inputProps={{ maxLength: 1000 }}
        />
        <IconButton onClick={handleSubmit} disabled={!draft.trim() || submitting} aria-label="Post comment">
          <SendIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

const ProfileView = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // react-router gives the initial history entry a "default" key - any entry
  // reached via client-side navigation (Link/navigate from elsewhere in the
  // app) gets a real key instead. So "default" means this page was loaded
  // directly (pasted/shared link or a hard refresh), with no in-app page to
  // go back to.
  const cameFromWithinApp = location.key !== 'default';

  const [profile,    setProfile]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [connStatus, setConnStatus] = useState(null); // null | { status, request_id, i_sent }
  const [connecting, setConnecting] = useState(false);
  const [addToCirclePrompt, setAddToCirclePrompt] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // index into profile.showcase_photos
  const [interactions, setInteractions] = useState({}); // photoKey -> { like_count, comment_count, liked_by_me }
  const [commentsOpen, setCommentsOpen] = useState(false);

  const isLoggedIn = !!api.getToken();
  const isOwnProfile = isLoggedIn && !!user?.customer_id && !!profile?.customer_id
    && user.customer_id === profile.customer_id;

  useEffect(() => {
    if (!username) return;
    setLoading(true);

    profileApi.getPublicProfile(username)
      .then(async (data) => {
        setProfile(data);
        // Only fetch connection status when logged in, viewing someone else's profile
        if (isLoggedIn && data.customer_id && data.customer_id !== user?.customer_id) {
          try {
            const status = await networkApi.getConnectionStatus(data.customer_id);
            setConnStatus(status);
          } catch {
            // Non-fatal - just won't show connection button
          }
        }
      })
      .catch(() => setError('Profile not found or not public.'))
      .finally(() => setLoading(false));
  }, [username, isLoggedIn, user?.customer_id]);

  // Batch-load like/comment counts + my like state for the showcase grid,
  // same pattern as Moments' Feeds view (see momentsApi.getInteractionCounts).
  useEffect(() => {
    const photos = profile?.showcase_photos;
    if (!profile?.can_view_photos || !photos?.length) return undefined;
    let cancelled = false;
    momentsApi.getInteractionCounts(photos.map((p) => ({ event_id: p.event_id, photo_id: p.photo_id })))
      .then((payload) => {
        if (!cancelled) setInteractions((prev) => ({ ...prev, ...(payload.counts || {}) }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [profile]);

  useEffect(() => {
    setCommentsOpen(false);
  }, [lightboxIndex]);

  const handleToggleLike = useCallback((eventId, photoId) => {
    const key = photoKey(eventId, photoId);
    let snapshot;
    setInteractions((prev) => {
      snapshot = prev[key];
      const cur = prev[key] || { like_count: 0, comment_count: 0, liked_by_me: false };
      const nextLiked = !cur.liked_by_me;
      return { ...prev, [key]: { ...cur, liked_by_me: nextLiked, like_count: Math.max(0, cur.like_count + (nextLiked ? 1 : -1)) } };
    });
    momentsApi.toggleLike(eventId, photoId)
      .then((result) => {
        setInteractions((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), liked_by_me: result.liked, like_count: result.like_count } }));
      })
      .catch(() => {
        setInteractions((prev) => ({ ...prev, [key]: snapshot || prev[key] }));
      });
  }, []);

  const handleCommentCountDelta = useCallback((eventId, photoId, delta) => {
    const key = photoKey(eventId, photoId);
    setInteractions((prev) => {
      const cur = prev[key] || { like_count: 0, comment_count: 0, liked_by_me: false };
      return { ...prev, [key]: { ...cur, comment_count: Math.max(0, cur.comment_count + delta) } };
    });
  }, []);

  // Double-tap-to-like on the lightbox image, same pattern as Moments'
  // FeedCard.handlePhotoTap (a tap-timer, since mobile browsers don't
  // reliably fire a native dblclick on a fast double-tap).
  const [heartBurst, setHeartBurst] = useState(false);
  const tapTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(tapTimerRef.current), []);

  const handleLightboxImageTap = (currentPhoto) => {
    if (!profile?.can_view_photos || !currentPhoto) return;
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
      tapTimerRef.current = null;
      const key = photoKey(currentPhoto.event_id, currentPhoto.photo_id);
      if (!interactions[key]?.liked_by_me) handleToggleLike(currentPhoto.event_id, currentPhoto.photo_id);
      setHeartBurst(true);
      setTimeout(() => setHeartBurst(false), 700);
      return;
    }
    tapTimerRef.current = setTimeout(() => { tapTimerRef.current = null; }, 250);
  };

  const handleConnect = async () => {
    if (!profile?.customer_id) return;
    setConnecting(true);
    try {
      const sent = await networkApi.sendConnectionRequest({ receiver_id: profile.customer_id });
      setConnStatus({ status: 'pending', request_id: sent.request_id, i_sent: true });
      setAddToCirclePrompt(true);
    } catch (err) {
      // If already exists, re-fetch actual status
      try {
        const status = await networkApi.getConnectionStatus(profile.customer_id);
        setConnStatus(status);
      } catch { /* ignore */ }
    } finally {
      setConnecting(false);
    }
  };

  const handleRevokeConnection = async () => {
    if (!connStatus?.request_id) return;
    setConnecting(true);
    try {
      await networkApi.revokeConnectionRequest(connStatus.request_id);
      setConnStatus({ status: 'none' });
    } catch {
      try {
        const status = await networkApi.getConnectionStatus(profile.customer_id);
        setConnStatus(status);
      } catch { /* ignore */ }
    } finally {
      setConnecting(false);
    }
  };

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Stack spacing={2} alignItems="center">
          <Skeleton variant="circular" width={120} height={120} />
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={140} height={24} />
        </Stack>
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        {cameFromWithinApp && (
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2, textTransform: 'none' }}>
            Back
          </Button>
        )}
        <Alert severity="error">{error || 'Profile not found.'}</Alert>
      </Container>
    );
  }

  const hasSocials = profile.instagram_handle || profile.facebook_url || profile.linkedin_url;

  // ── Connection button ────────────────────────────────────────────────────────
  const renderConnectionAction = () => {
    if (!isLoggedIn || !profile.customer_id || isOwnProfile) return null;

    if (!connStatus || connStatus.status === 'none') {
      return (
        <Button
          variant="contained"
          startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          onClick={handleConnect}
          disabled={connecting}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Connect
        </Button>
      );
    }

    if (connStatus.status === 'pending') {
      return connStatus.i_sent ? (
        <Chip
          icon={connecting ? <CircularProgress size={16} /> : <HourglassEmptyIcon />}
          label="Request sent"
          variant="outlined"
          color="default"
          onDelete={handleRevokeConnection}
          deleteIcon={<CloseIcon />}
        />
      ) : (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Respond to their request"
          variant="outlined"
          color="primary"
          onClick={() => navigate('/network')}
          clickable
        />
      );
    }

    if (connStatus.status === 'accepted') {
      return (
        <Chip
          icon={<CheckCircleOutlineIcon />}
          label="Connected"
          color="success"
          variant="outlined"
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ bgcolor: 'background.default', pt: 2 }}>
      <Container maxWidth="sm">
        {/* Back button - only when there's somewhere in the app to go back to */}
        {cameFromWithinApp && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}
          >
            Back
          </Button>
        )}

        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
          {/* Cover gradient */}
          <Box
            sx={{
              height: 120,
              background: 'linear-gradient(135deg, #C73866 0%, #FE676E 50%, #FD8F52 100%)',
            }}
          />

          <Box sx={{ px: 3, pb: 3 }}>
            {/* Avatar + connect action row */}
            <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mt: -5, mb: 1 }}>
              <Avatar
                src={profile.profile_picture}
                sx={{
                  width: 96, height: 96,
                  border: '4px solid white',
                  fontSize: 36,
                  bgcolor: '#C73866',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                }}
              >
                {(profile.customer_name || '?')[0].toUpperCase()}
              </Avatar>

              <Box sx={{ pb: 0.5 }}>
                {renderConnectionAction()}
              </Box>
            </Stack>

            {/* Name & username */}
            <Stack direction="row" alignItems="center" spacing={1} mt={1.5} mb={0.5}>
              <Typography variant="h5" fontWeight={700}>
                {profile.customer_name}
              </Typography>
              <Tooltip title="Public profile">
                <PublicIcon fontSize="small" color="action" />
              </Tooltip>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" mb={2}>
              <Typography variant="body2" color="text.secondary">
                @{profile.username}
              </Typography>
              {profile.gender && (
                <>
                  <Typography color="text.disabled">·</Typography>
                  <Typography variant="body2" color="text.secondary">{profile.gender}</Typography>
                </>
              )}
            </Stack>

            {/* Social links */}
            {hasSocials && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1}>
                  {profile.instagram_handle && (
                    <Tooltip title={profile.instagram_handle}>
                      <IconButton
                        component={Link}
                        href={`https://instagram.com/${profile.instagram_handle.replace('@', '')}`}
                        target="_blank"
                        size="small"
                        sx={{ color: '#E1306C' }}
                      >
                        <InstagramIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {profile.facebook_url && (
                    <Tooltip title="Facebook">
                      <IconButton
                        component={Link}
                        href={profile.facebook_url.startsWith('http') ? profile.facebook_url : `https://${profile.facebook_url}`}
                        target="_blank"
                        size="small"
                        sx={{ color: '#1877F2' }}
                      >
                        <FacebookIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {profile.linkedin_url && (
                    <Tooltip title="LinkedIn">
                      <IconButton
                        component={Link}
                        href={profile.linkedin_url.startsWith('http') ? profile.linkedin_url : `https://${profile.linkedin_url}`}
                        target="_blank"
                        size="small"
                        sx={{ color: '#0A66C2' }}
                      >
                        <LinkedInIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </>
            )}

            <Typography variant="caption" color="text.disabled" display="block" mt={2}>
              Address and contact info are visible to connected network members only.
            </Typography>
          </Box>
        </Paper>

        {/* Showcase photo grid ("Add to Profile View") - CircleMoments isn't a
            public photo feed, so this is only ever shown to the profile owner
            or an accepted network connection (see can_view_photos, set by
            GET /public/profile/<username>). Everyone else gets a locked
            prompt instead, regardless of whether photos exist. */}
        {profile.can_view_photos ? (
          profile.showcase_photos?.length > 0 && (
            <Box sx={{ mt: 3, mb: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <GridOnIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                  Photos
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '2px',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {profile.showcase_photos.map((photo, idx) => (
                  <Box
                    key={`${photo.event_id}-${photo.photo_id}`}
                    onClick={() => setLightboxIndex(idx)}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      cursor: 'pointer',
                      bgcolor: 'action.hover',
                      '&:hover img': { opacity: 0.85 },
                    }}
                  >
                    <Box
                      component="img"
                      src={photo.thumbnail_url}
                      alt={photo.event_name || 'Photo'}
                      loading="lazy"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'opacity 0.15s',
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          )
        ) : (
          <Box sx={{ mt: 3, mb: 4 }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
              <GridOnIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
                Photos
              </Typography>
            </Stack>
            <Paper variant="outlined" sx={{ py: 4, px: 3, textAlign: 'center', borderRadius: 2, bgcolor: 'action.hover' }}>
              <LockOutlinedIcon color="disabled" sx={{ fontSize: 32, mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                You need to be connected with {profile.customer_name} to view their photos.
              </Typography>
            </Paper>
          </Box>
        )}
      </Container>

      {/* Photo previewer popup */}
      <Dialog
        open={lightboxIndex !== null}
        onClose={() => setLightboxIndex(null)}
        maxWidth="md"
        fullWidth
      >
        {lightboxIndex !== null && profile.showcase_photos?.[lightboxIndex] && (() => {
          const currentPhoto = profile.showcase_photos[lightboxIndex];
          const key = photoKey(currentPhoto.event_id, currentPhoto.photo_id);
          const interaction = interactions[key];
          const liked = Boolean(interaction?.liked_by_me);
          const likeCount = interaction?.like_count ?? 0;
          const commentCount = interaction?.comment_count ?? 0;

          return (
            <>
              <DialogContent sx={{ p: 0, bgcolor: 'grey.950', position: 'relative' }}>
                <IconButton
                  onClick={() => setLightboxIndex(null)}
                  sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', zIndex: 1 }}
                >
                  <CloseIcon />
                </IconButton>
                {lightboxIndex > 0 && (
                  <IconButton
                    onClick={() => setLightboxIndex(lightboxIndex - 1)}
                    sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', color: 'common.white', zIndex: 1 }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                )}
                {lightboxIndex < profile.showcase_photos.length - 1 && (
                  <IconButton
                    onClick={() => setLightboxIndex(lightboxIndex + 1)}
                    sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', color: 'common.white', zIndex: 1 }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                )}
                <Box
                  onClick={() => handleLightboxImageTap(currentPhoto)}
                  sx={{
                    position: 'relative',
                    cursor: profile.can_view_photos ? 'pointer' : 'default',
                    WebkitTapHighlightColor: 'transparent',
                    WebkitUserSelect: 'none',
                    userSelect: 'none',
                  }}
                >
                  <Box
                    component="img"
                    src={currentPhoto.preview_url || currentPhoto.thumbnail_url}
                    alt={currentPhoto.event_name || 'Photo'}
                    sx={{
                      width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block',
                      WebkitUserDrag: 'none', WebkitTouchCallout: 'none',
                    }}
                  />
                  <AnimatePresence>
                    {heartBurst && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1.15 }}
                        exit={{ opacity: 0, scale: 1.3 }}
                        transition={{ duration: 0.35 }}
                        style={{
                          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                          pointerEvents: 'none',
                        }}
                      >
                        <FavoriteIcon sx={{ fontSize: 96, color: 'rgba(255,255,255,0.92)', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.45))' }} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </DialogContent>
              {profile.can_view_photos && (
                <>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleLike(currentPhoto.event_id, currentPhoto.photo_id)}
                      aria-label="Like photo"
                    >
                      {liked ? <FavoriteIcon fontSize="small" color="error" /> : <FavoriteBorderIcon fontSize="small" />}
                    </IconButton>
                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 14 }}>{likeCount}</Typography>

                    <IconButton size="small" onClick={() => setCommentsOpen((v) => !v)} aria-label="Toggle comments" sx={{ ml: 0.5 }}>
                      <ChatBubbleOutlineIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" fontWeight={700} sx={{ minWidth: 14 }}>{commentCount}</Typography>
                  </Stack>
                  {commentsOpen && (
                    <PhotoCommentsPanel
                      eventId={currentPhoto.event_id}
                      photoId={currentPhoto.photo_id}
                      onCommentCountDelta={(delta) => handleCommentCountDelta(currentPhoto.event_id, currentPhoto.photo_id, delta)}
                    />
                  )}
                </>
              )}
              <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {currentPhoto.event_name}
                </Typography>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      <AddToCirclePrompt
        open={addToCirclePrompt}
        onClose={() => setAddToCirclePrompt(false)}
        requestId={connStatus?.request_id}
        personName={profile?.customer_name}
      />
    </Box>
  );
};

export default ProfileView;
